import { Injectable } from '@angular/core';
import { IndexExportFormat } from '@libs/shared/domain/aggregations/index/index-export-format.schema';
import { OpenSearchRequest, OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { PROJECTION_INDEXES } from '@libs/shared/infrastructure/projection_indexes';
import { CopayCodeRequestUtilityService } from '@services/copay-code-request-utility.service';
import { PatientAssistanceRequestViewModel } from '@services/models/patient-assistance-request-view.model';
import { CodeRequestResultType } from '@shared/models/code-request-result.enum';
import { OpenSearchAggsValueResult, OpenSearchPage, OpenSearchQueryResult, OpenSearchSimpleSort } from '@shared/providers/opensearch-api';
import { OpenSearchClient } from '@shared/providers/opensearch-client';
import { OpenSearchService } from '@shared/providers/opensearch.service';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// The flight board draws a route, so a claim only counts once both of its ends are located.
// Shared by the search and the count so the board's odometer matches what it can actually fly.
const FLIGHT_CLAIM_FILTER: OpenSearchRequestFilter[] = [
  { field: 'pharmacyLat', operator: 'exists' },
  { field: 'pharmacyLon', operator: 'exists' },
  { field: 'prescriberLat', operator: 'exists' },
  { field: 'prescriberLon', operator: 'exists' },
];

@Injectable({ providedIn: 'root' })
export class PatientAssistanceRequestSearchService extends OpenSearchService {
  readonly indexName = PROJECTION_INDEXES.PATIENT_ASSISTANCE_REQUEST_GENERAL_VIEW;
  readonly dataviewColumns = 'patient_assistance_request_general_columns';

  countData(filter: OpenSearchRequestFilter[]) {
    return this.client.count(this.indexName, { filter: this.patchStatusFilter(filter) });
  }

  searchData(
    filter: OpenSearchRequestFilter[],
    page: OpenSearchPage,
    sort: OpenSearchSimpleSort,
    fields?: string[],
  ): Observable<OpenSearchQueryResult<PatientAssistanceRequestViewModel>> {
    const query: OpenSearchRequest = {
      filter: this.patchStatusFilter(filter),
      page,
      sort: this.createSearchSort(sort),
      select: fields,
    };
    return this.client.search<PatientAssistanceRequestViewModel>(this.indexName, query).pipe(
      catchError(error => this.processSearchError<PatientAssistanceRequestViewModel>(error)),
      map(result => {
        result.items = result.items.map(item => this.mapItem(item));
        return result;
      }),
    );
  }

  public exportData(
    filter: OpenSearchRequestFilter[],
    sort: OpenSearchSimpleSort,
    fields?: string[],
    format?: IndexExportFormat,
    dataviewColumns?: string,
    allColumns?: boolean,
  ): Observable<{ fileUrl: string }> {
    return this.client.export(
      this.indexName,
      {
        filter: this.patchStatusFilter(filter),
        sort: this.createSearchSort(sort),
        select: fields,
      },
      {
        dataview: this.dataviewColumns,
        ...OpenSearchClient.exportAllColumnsParam(allColumns),
      },
    );
  }

  getCcrById(id: string, fields?: string[]): Observable<OpenSearchQueryResult<PatientAssistanceRequestViewModel>> {
    const query: OpenSearchRequest = {
      page: { from: 0, size: 1 },
      filter: [
        {
          field: 'id',
          operator: 'equal',
          value: id,
        },
      ],
      select: fields,
    };
    return this.client.search<PatientAssistanceRequestViewModel>(this.indexName, query).pipe(
      catchError(error => this.processSearchError<PatientAssistanceRequestViewModel>(error)),
      map(result => {
        result.items = result.items.map(item => this.mapItem(item));
        return result;
      }),
    );
  }

  getCcrsByRxNumber(rxNumber: string): Observable<OpenSearchQueryResult<PatientAssistanceRequestViewModel>> {
    return this.searchData(
      [
        {
          field: 'rxNumber',
          value: rxNumber,
          operator: 'contains',
        },
      ],
      {
        from: 0,
        size: 100,
      },
      {
        created: 'desc',
      },
    );
  }

  getClaimsCount(filter: OpenSearchRequestFilter[]): Observable<number> {
    return this.client
      .aggs(this.indexName, {
        filter: this.patchStatusFilter(filter),
        aggs: { claims: { field: 'id', operator: 'count' } },
      })
      .pipe(
        map(result => (result.claims as OpenSearchAggsValueResult).value ?? 0),
        catchError(error => {
          this.logger.error(error);
          return of(0);
        }),
      );
  }

  getClaimsCountWithPrevious(filter: OpenSearchRequestFilter[]): Observable<{ current: number; previous: number }> {
    return forkJoin({
      current: this.getClaimsCount(filter),
      previous: this.getClaimsCount(this.getFilterWithPrevious(filter)),
    });
  }

  getPharmaciesCount(filter: OpenSearchRequestFilter[]): Observable<number> {
    return this.client
      .aggs(this.indexName, {
        filter: this.patchStatusFilter(filter),
        aggs: { pharmacies: { field: 'pharmacyNcpdp', operator: 'unique' } },
      })
      .pipe(
        map(result => (result.pharmacies as OpenSearchAggsValueResult).value ?? 0),
        catchError(error => {
          this.logger.error(error);
          return of(0);
        }),
      );
  }

  getPharmaciesCountWithPrevious(filter: OpenSearchRequestFilter[]): Observable<{ current: number; previous: number }> {
    return forkJoin({
      current: this.getPharmaciesCount(filter),
      previous: this.getPharmaciesCount(this.getFilterWithPrevious(filter)),
    });
  }

  getSystemStats(filter: OpenSearchRequestFilter[]): Observable<{ epm: number; load15: number }> {
    return forkJoin({
      epm: this.client
        .aggs(this.indexName, {
          filter: this.patchStatusFilter(this.getTrailingWindowFilter(filter, 1, 'EPM')),
          aggs: { epm: { field: 'workflowPathCount', operator: 'sum' } },
        })
        .pipe(map(result => result.epm as OpenSearchAggsValueResult)),
      load15: this.client
        .aggs(this.indexName, {
          filter: this.patchStatusFilter(this.getTrailingWindowFilter(filter, 15, 'Load 15')),
          aggs: { load15: { field: 'workflowPathCount', operator: 'sum' } },
        })
        .pipe(map(result => result.load15 as OpenSearchAggsValueResult)),
    }).pipe(
      map(results => ({
        epm: results.epm.value ?? 0,
        load15: results.load15.value ?? 0,
      })),
      catchError(error => {
        this.logger.error(error);
        return of({ epm: 0, load15: 0 });
      }),
    );
  }

  private mapItem(item: PatientAssistanceRequestViewModel) {
    if (item.status === CodeRequestResultType.Consignment) {
      item.status = CodeRequestResultType.CashEligible;
    }
    const providerName = `${item.prescriberName ?? ''}`;
    const providerNpi = `${item.prescriberNpi === 'None' ? '' : item.prescriberNpi}${providerName.length > 1 ? ' - ' : ''}${providerName}`;
    return {
      ...item,
      providerNpi,
      statusClass: `bg-${CopayCodeRequestUtilityService.getCopayRequestStatusClass(item)}`,
      rxNumber: item.rxNumber === 'None' ? '' : item.rxNumber,
      prescriberNpi: item.prescriberNpi === 'None' ? '' : item.prescriberNpi,
    };
  }

  private patchStatusFilter(filter: OpenSearchRequestFilter[]): OpenSearchRequestFilter[] {
    return filter.map(item => {
      if (item.field !== 'status') return item;

      const values: string[] = Array.isArray(item.value) ? item.value : item.value === undefined ? [] : [item.value];
      const list = new Set<string>(values);
      if (list.has(CodeRequestResultType.CashEligible)) {
        list.add(CodeRequestResultType.Consignment);
      }
      return { ...item, operator: 'in' as const, value: Array.from(list) };
    });
  }

  private getTrailingWindowFilter(filter: OpenSearchRequestFilter[], minutes: number, label: string): OpenSearchRequestFilter[] {
    const createdFilter = filter.find(f => f.field === 'created' && f.operator === 'range');

    if (!createdFilter) {
      console.error(`No created date range filter found. Cannot compute ${label} filter.`);
      return filter;
    }

    const end = moment();
    const windowFilter: OpenSearchRequestFilter = {
      field: 'created',
      operator: 'range',
      value: {
        created: {
          gte: moment(end).subtract(minutes, 'minutes').toISOString(),
          lte: end.toISOString(),
        },
      },
    };

    return [...filter.filter(f => f.field !== 'created'), windowFilter];
  }

  countFlightClaims(filter: OpenSearchRequestFilter[]): Observable<number> {
    return this.getClaimsCount([...filter, ...FLIGHT_CLAIM_FILTER]);
  }

  searchFlightClaims(
    filter: OpenSearchRequestFilter[],
    size: number,
  ): Observable<OpenSearchQueryResult<PatientAssistanceRequestViewModel>> {
    return this.searchData([...filter, ...FLIGHT_CLAIM_FILTER], { from: 0, size }, { created: 'asc' }, [
      'created',
      'rxNumber',
      'pharmacyNcpdp',
      'pharmacyName',
      'pharmacyLat',
      'pharmacyLon',
      'prescriberNpi',
      'prescriberName',
      'prescriberLat',
      'prescriberLon',
    ]);
  }
}
